import './host'

import { MembershipService } from '@graft/nuget-gc.webportals.memberships'

import { getSessionToken } from '@common/helpers'
import { isVisionLocal } from '@common/utils/isVisionLocal'
import { readGraftObject } from '@common/utils/readGraftObject'

import type { InvitationOperationResult } from '@modules/invitation/invitation.interface'
import { mapGraftInvitationOperationResult } from '@modules/invitation/mappers/mapGraftInvitationOperationResult'
import { hasInvitationLinkParams } from '@modules/invitation/utils/hasInvitationLinkParams'

import type { Invitation } from './memberships.type'
import { isMembershipsServiceConfigured } from './memberships.utils'

const emptyInvitationOperationResult = (): InvitationOperationResult => ({
  succeeded: false,
  outcome: '',
  message: '',
  invitationId: '',
  operationId: '',
})

export async function getInvitations(): Promise<Invitation[]> {
  try {
    if (isVisionLocal() || !isMembershipsServiceConfigured()) {
      return []
    }

    const token = await getSessionToken()
    const invitations = await MembershipService.getInvitations(token)

    if (!invitations?.length) {
      return []
    }

    return Promise.all(invitations.map((invitation) => readGraftObject<Invitation>(invitation)))
  } catch (error) {
    console.error('Failed to get invitations:', error)
    return []
  }
}

export async function acceptInvitation(
  invitationId: string,
  expiresAtUnix?: string,
  signature?: string
): Promise<InvitationOperationResult> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return emptyInvitationOperationResult()
  }

  try {
    const token = await getSessionToken()
    const raw = hasInvitationLinkParams(expiresAtUnix, signature)
      ? await MembershipService.acceptInvitationFromLink(token, invitationId, expiresAtUnix ?? '', signature ?? '')
      : await MembershipService.acceptInvitation(token, invitationId)

    return mapGraftInvitationOperationResult(raw)
  } catch (error) {
    console.error('Failed to accept invitation:', error)
    throw error
  }
}

export async function cancelInvitation(invitationId: string): Promise<InvitationOperationResult> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return emptyInvitationOperationResult()
  }

  try {
    const token = await getSessionToken()
    const raw = await MembershipService.cancelInvitation(token, invitationId)
    return mapGraftInvitationOperationResult(raw)
  } catch (error) {
    console.error('Failed to cancel invitation:', error)
    throw error
  }
}

export async function inviteWorkspaceMember(
  workspaceId: string,
  membershipLevel: string,
  email: string,
  region: string
): Promise<InvitationOperationResult> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return emptyInvitationOperationResult()
  }

  try {
    const token = await getSessionToken()
    const raw = await MembershipService.inviteWorkspaceMember(token, workspaceId, membershipLevel, email, region)
    return mapGraftInvitationOperationResult(raw)
  } catch (error) {
    console.error('Failed to invite workspace member:', error)
    throw error
  }
}

export async function inviteProjectMember(
  projectId: string,
  membershipLevel: string,
  email: string,
  region: string
): Promise<InvitationOperationResult> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return emptyInvitationOperationResult()
  }

  try {
    const token = await getSessionToken()
    const raw = await MembershipService.inviteProjectMember(token, projectId, membershipLevel, email, region)
    return mapGraftInvitationOperationResult(raw)
  } catch (error) {
    console.error('Failed to invite project member:', error)
    throw error
  }
}

async function assertMembershipSuccess(success: boolean, errorMessage: string): Promise<void> {
  if (!success) {
    throw new Error(errorMessage)
  }
}

export async function deleteWorkspaceMembership(workspaceId: string, deletedUserId: string): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.deleteWorkspaceMembership(token, workspaceId, deletedUserId),
      'Failed to remove workspace member'
    )
  } catch (error) {
    console.error('Failed to remove workspace member:', error)
    throw error
  }
}

export async function deleteProjectMembership(projectId: string, deletedUserId: string): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.deleteProjectMembership(token, projectId, deletedUserId),
      'Failed to remove project member'
    )
  } catch (error) {
    console.error('Failed to remove project member:', error)
    throw error
  }
}
